/** Finite, worker-owned tropical coastal colonies. All placement uses the final
 * terrain/support sampler; no meshes, scene state or frame-time queries. */
import { coastalDirection, coastalShore, createSurfaceColumn, FACES, rngFromString,
  seaSurface, surfaceColumnFromSample, type CoastalLandform, type PlanetDef,
  type PlanetSampler, type TerrainSample } from '@hyperlight/shared';
import { groundMud, groundSnow, hasLivingGround } from '../groundSurface';

export type CoastalKind = 'palm' | 'grass' | 'shrub';
type V3 = readonly [number, number, number];
type Tile = {face:number;level:number;u0:number;v0:number;size:number};
interface Candidate {
  id:string; featureId:string; kind:CoastalKind; face:number;u:number;v:number;
  dir:V3; alongM:number; inlandM:number; size:number;yaw:number;variant:number;
  priority:number;climateRoll:number;
}
export interface CoastalPlacement extends Candidate {
  radius:number;normal:V3;depth:number;temp:number;relief:number;footprint:number;
}
export type CoastalData = CoastalPlacement[];
export const COASTAL_LIMITS = {palm:48,grass:192,shrub:96} as const;
export const COASTAL_MIN_DRY_M = {palm:2.1,grass:1.35,shrub:1.7} as const;
const fresh = ():TerrainSample => ({h:0,temp:0,moist:0,river:0,mountain:0,town:0,road:8,flowx:0,flowy:0,flowz:0});
const column=createSurfaceColumn();
const smooth=(a:number,b:number,x:number):number=>{const t=Math.min(1,Math.max(0,(x-a)/(b-a)));return t*t*(3-2*t);};
const norm=(x:number,y:number,z:number):V3=>{const l=Math.hypot(x,y,z);return [x/l,y/l,z/l];};

/** Warm, dry, living soil only. The signed water query excludes inland water
 * as well as ocean water. Snow, mud, river cores and settlement access retain
 * their own composition; palm islands never turn into mangrove thickets. */
export function coastalAllowed(kind:CoastalKind,def:PlanetDef,A:number,ts:TerrainSample,footprint=0):boolean {
  if(!hasLivingGround(def)||def.seaRadius===null||seaSurface(def)!=='water'||ts.temp<=.16||ts.temp>=.87
    ||ts.moist<-.36||ts.town>.002||ts.road<3+footprint||ts.river>.18
    ||(ts.glacierIceM??0)>.02||(ts.lavaDepthM??0)>0)return false;
  const h=ts.h*A,above=def.radius+h-def.seaRadius;
  if(groundSnow(ts.temp,h,def.climate.snowline*A)>.04||groundMud(above,ts.estuaryShelter??0,ts.temp)>.08)return false;
  return surfaceColumnFromSample(def,A,ts,column).waterDepth<=-COASTAL_MIN_DRY_M[kind];
}

function faceUV(d:V3):{face:number;u:number;v:number} {
  let face=0,dot=-Infinity;
  for(let i=0;i<6;i++){const n=FACES[i].normal,k=d[0]*n[0]+d[1]*n[1]+d[2]*n[2];if(k>dot){face=i;dot=k;}}
  const f=FACES[face];return {face,u:(d[0]*f.right[0]+d[1]*f.right[1]+d[2]*f.right[2])/dot,
    v:(d[0]*f.up[0]+d[1]*f.up[1]+d[2]*f.up[2])/dot};
}

interface Colony {u:number;v:number;ru:number;rv:number;strength:number}
interface Layout {colonies:Colony[];start:number;end:number;maxInland:number;corridor:number}
const candidates=new WeakMap<CoastalLandform,readonly Candidate[]>();
const layouts=new WeakMap<CoastalLandform,Layout>();

/** Group contours cross candidate-grid boundaries. Unequal elliptical colonies
 * and a continuous cross-shore gap preserve exposed sand and access to brows. */
function layout(f:CoastalLandform):Layout {
  const cached=layouts.get(f);if(cached)return cached;
  const colonies:Colony[]=[],start=-f.halfLengthM*.91,end=f.halfLengthM*.91;
  const scale=Math.min(1,f.inlandM/450),maxInland=Math.min(f.inlandM*.66,f.beachM+185*scale);
  // f.id is a stable feature address; f.seed carries the actual world seed.
  const rng=rngFromString(`${f.id}:${f.seed}:tropical-colonies:1`);
  for(let u=start+30;u<end;u+=82+rng()*52){
    const center=u+(rng()-.5)*28;
    const back=f.kind==='shelving-beach'?f.beachM*(.8+rng()*.6):f.kind==='rocky-headland'?30+42*rng():f.kind==='cliff-coast'?44+25*rng():f.beachM*(.62+rng()*.27);
    colonies.push({u:center,v:back,ru:25+rng()*24,rv:12+rng()*17,strength:.72+rng()*.26});
    if(f.kind!=='shelving-beach'&&rng()>.20)colonies.push({u:center+(rng()-.5)*48,
      v:Math.min(maxInland-18,(f.kind==='pocket-beach'?f.beachM:0)+(f.kind==='rocky-headland'?76:74)*scale+rng()*55*scale),
      ru:30+rng()*32,rv:17+rng()*25,strength:.48+rng()*.36});
  }
  const result={colonies,start,end,maxInland,corridor:f.kind==='pocket-beach'?-.24*f.halfLengthM:(rngFromString(`${f.id}:${f.seed}:tropical-corridor`)()-.5)*f.halfLengthM*.75};
  layouts.set(f,result);return result;
}
function colonyDensity(l:Layout,u:number,cross:number,margin=0):number {
  if(u<l.start||u>l.end||cross<0||cross>l.maxInland||Math.abs(u-l.corridor)<13)return 0;
  let density=0;
  for(const c of l.colonies){const d=Math.hypot((u-c.u)/(c.ru+margin),(cross-c.v)/(c.rv+margin));density=Math.max(density,(1-smooth(.32,1.18,d))*c.strength);}
  return density;
}

// Coarse Cartesian bins cover only finite reaches, shared by the three flora
// bands. No sampling or per-candidate allocation is needed for forest masking.
const reachIndices=new WeakMap<PlanetSampler,Map<string,CoastalLandform[]>>();
const indexKey=(x:number,y:number,z:number):string=>`${Math.floor(x/800)},${Math.floor(y/800)},${Math.floor(z/800)}`;
function reachIndex(def:PlanetDef,sampler:PlanetSampler):Map<string,CoastalLandform[]> {
  let index=reachIndices.get(sampler);if(index)return index;
  index=new Map();
  for(const f of sampler.coasts()){
    const bound=Math.hypot(f.halfLengthM,f.inlandM)*1.08+10;
    const lo=f.center.map(n=>Math.floor((n*def.radius-bound)/800)),hi=f.center.map(n=>Math.floor((n*def.radius+bound)/800));
    for(let x=lo[0];x<=hi[0];x++)for(let y=lo[1];y<=hi[1];y++)for(let z=lo[2];z<=hi[2];z++){
      const key=`${x},${y},${z}`,list=index.get(key);if(list)list.push(f);else index.set(key,[f]);
    }
  }
  reachIndices.set(sampler,index);return index;
}

/** Ordinary canopy gives the palm colonies breathing room in every streaming
 * band. Generic forest outside these small, warm dry contours is untouched. */
export function coastalForestWeight(def:PlanetDef,sampler:PlanetSampler,dx:number,dy:number,dz:number,ts:TerrainSample):number {
  if(!coastalAllowed('palm',def,sampler.amplitude,ts))return 0;
  const reaches=reachIndex(def,sampler).get(indexKey(dx*def.radius,dy*def.radius,dz*def.radius));
  if(!reaches)return 0;
  let weight=0;
  for(const f of reaches){
    const facing=dx*f.center[0]+dy*f.center[1]+dz*f.center[2];if(facing<=0)continue;
    const u=(dx*f.along[0]+dy*f.along[1]+dz*f.along[2])*def.radius/facing;
    if(Math.abs(u)>=f.halfLengthM)continue;
    const v=(dx*f.inland[0]+dy*f.inland[1]+dz*f.inland[2])*def.radius/facing;
    // Ordinary mature crowns reach well beyond their trunk positions. Reserve
    // a whole crown's breathing room around a palm colony, while retaining the
    // same coast bounds, access gaps and tapered colony contours.
    weight=Math.max(weight,smooth(.03,.30,colonyDensity(layout(f),u,v-coastalShore(f,u),28)));
  }
  return weight*smooth(.16,.22,ts.temp)*(1-smooth(.72,.87,ts.temp));
}

function cells(f:CoastalLandform,R:number):readonly Candidate[] {
  const cached=candidates.get(f);if(cached)return cached;
  const l=layout(f),{start,end,maxInland}=l,result:Candidate[]=[];
  for(const spacing of [12,5]) {
    const countU=Math.ceil((end-start)/spacing),countV=Math.ceil(maxInland/spacing);
    for(let j=0;j<countV;j++)for(let i=0;i<countU;i++) {
      const id=`${f.id}:tropical1:${f.seed}:${spacing}:${i}:${j}`,random=rngFromString(id);
      const u=start+(i+.15+random()*.7)*spacing,cross=(j+.12+random()*.76)*spacing;
      // The existing pocket-beach access gully must remain continuously open.
      // Other reaches receive their own 13–20 m cross-shore walking corridor.
      if(Math.abs(u-l.corridor)<(spacing===12?13:9))continue;
      const density=colonyDensity(l,u,cross);
      const kind:CoastalKind=spacing===12?'palm':random()<.39?'shrub':'grass';
      // Fill more of each irregular colony, keeping the same candidate roots,
      // shoreline gaps and separate low-cover stream.
      if(random()>density*(kind==='palm'?1.15:.92))continue;
      const v=coastalShore(f,u)+cross;
      if(v>=f.inlandM*.82||v<=-f.offshoreM*.8)continue;
      const dir=coastalDirection(f,R,u,v);
      result.push({id,featureId:f.id,kind,...faceUV(dir),dir,alongM:u,inlandM:v,
        size:kind==='palm'?.88+random()*.30:kind==='shrub'?.95+random()*1.05:1.15+random()*1.05,
        yaw:random()*Math.PI*2,variant:Math.floor(random()*(kind==='palm'?4:2)),priority:random(),climateRoll:random()});
    }
  }
  result.sort((a,b)=>a.priority-b.priority||(a.id<b.id?-1:a.id>b.id?1:0));
  candidates.set(f,result);return result;
}

const PROBES:readonly (readonly [number,number])[]=[[1,0],[-1,0],[0,1],[0,-1],[Math.SQRT1_2,Math.SQRT1_2],[-Math.SQRT1_2,Math.SQRT1_2],[-Math.SQRT1_2,-Math.SQRT1_2],[Math.SQRT1_2,-Math.SQRT1_2]];

export function buildCoastalData(def:PlanetDef,sampler:PlanetSampler,tile:Tile,palmOnly=false):CoastalData {
  const result:CoastalData=[];
  if(!hasLivingGround(def)||def.seaRadius===null||seaSurface(def)!=='water')return result;
  const A=sampler.amplitude,ts=fresh(),pt=fresh(),counts={palm:0,grass:0,shrub:0};
  const basis=FACES[tile.face],u=tile.u0+tile.size*.5,v=tile.v0+tile.size*.5;
  const middle=norm(...basis.normal.map((n,i)=>n+basis.right[i]*u+basis.up[i]*v) as [number,number,number]);
  for(const f of sampler.coasts()) {
    const reach=def.radius*tile.size+Math.hypot(f.halfLengthM,f.inlandM);
    if(f.center.reduce((sum,n,i)=>sum+(n-middle[i])**2,0)*def.radius**2>reach**2)continue;
    for(const c of cells(f,def.radius)) {
      if(palmOnly&&c.kind!=='palm')continue;
      if(c.face!==tile.face||c.u<tile.u0||c.u>=tile.u0+tile.size||c.v<tile.v0||c.v>=tile.v0+tile.size||counts[c.kind]>=COASTAL_LIMITS[c.kind])continue;
      sampler.sample(...c.dir,ts,true);
      const footprint=c.size*(c.kind==='palm'?1.05:c.kind==='shrub'?1.05:.65);
      if(!coastalAllowed(c.kind,def,A,ts,footprint)||c.climateRoll>smooth(.16,c.kind==='palm'?.23:.25,ts.temp)*(1-smooth(.72,.87,ts.temp)))continue;
      const [x,y,z]=c.dir,ex=Math.abs(y)<.9?norm(-z,0,x):norm(0,z,-y);
      const ez:[number,number,number]=[y*ex[2]-z*ex[1],z*ex[0]-x*ex[2],x*ex[1]-y*ex[0]],r=def.radius+ts.h*A;
      let low=0,high=0,valid=true;const heights:number[]=[];
      for(const [a,b] of PROBES.slice(0,c.kind==='palm'?8:4)) {
        const d=norm(...c.dir.map((p,i)=>p+(ex[i]*a+ez[i]*b)*footprint/r) as [number,number,number]);
        sampler.sample(...d,pt,true);
        if(!coastalAllowed(c.kind,def,A,pt)){valid=false;break;}
        const delta=(pt.h-ts.h)*A;heights.push(delta);low=Math.min(low,delta);high=Math.max(high,delta);
      }
      // Palms are radially upright with curved stems: steep slope cannot be
      // hidden by tilting a whole tall tree. Low cover follows the support plane.
      if(!valid||high-low>(c.kind==='palm'?.72:.46))continue;
      const gx=(heights[0]-heights[1])/(2*footprint),gz=(heights[2]-heights[3])/(2*footprint);
      if(Math.hypot(gx,gz)>(c.kind==='palm'?.38:.42))continue;
      const normal=c.kind==='palm'?c.dir:norm(...c.dir.map((p,i)=>p-gx*ex[i]-gz*ez[i]) as [number,number,number]);
      // The authored stem ring extends 24cm below zero. Use half that skirt
      // to seat a sloping root bed without burying the uphill trunk by a metre.
      result.push({...c,radius:r+(c.kind==='palm'?Math.min(0,low+.12*c.size):0)-.035,normal,
        depth:surfaceColumnFromSample(def,A,ts,column).waterDepth,temp:ts.temp,relief:high-low,footprint});
      counts[c.kind]++;
    }
  }
  return result;
}

/** The horizon cohort is an exact union of the four forest owners. Re-running
 * the bounded child builds preserves each child's palm cap and transform, so
 * streaming cannot invent a different stand on either side of the crossfade. */
export function buildCoastalFarData(def:PlanetDef,sampler:PlanetSampler,tile:Tile):CoastalData {
  const result:CoastalData=[],size=tile.size*.5;
  for(let j=0;j<2;j++)for(let i=0;i<2;i++)result.push(...buildCoastalData(def,sampler,
    {face:tile.face,level:tile.level+1,u0:tile.u0+i*size,v0:tile.v0+j*size,size},true));
  return result;
}
